/*
 * MISS BULLITT — mobile photo gallery
 * Theme asset: assets/mb-gallery.js
 *
 * On phones the story beat's floating photo collage collapses into one
 * horizontal rail that drifts right-to-left and can be dragged.
 *
 * The track is duplicated once, so there is always a second copy queued to the
 * right. Offset is kept modulo the width of ONE copy, so the wrap is invisible,
 * nothing is re-appended, and the DOM does not grow however long it runs.
 *
 * Dragging sets the offset directly and banks velocity; on release the throw
 * decays back into the steady drift rather than stopping dead.
 */
(function () {
  'use strict';

  /* Run once. This file is loaded from mb-story.liquid, so a page with five
     beats executes it five times. The per-element `__mbGallery` guard below
     already stops a rail being built twice, but the document- and window-level
     listeners would still be registered five times over. */
  if (window.__mbGalleryLoaded) return;
  window.__mbGalleryLoaded = true;

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function num(el, n, d) {
    var v = parseFloat(el.getAttribute('data-' + n));
    return isNaN(v) ? d : v;
  }

  function Gallery(media) {
    this.media = media;
    this.speed = num(media, 'gal-speed', 26);        // px per second, leftwards
    this.breakpoint = num(media, 'gal-breakpoint', 749);
    this.built = false;
    this.offset = 0;
    this.vel = 0;
    this.dragging = false;
    this.raf = 0;
    this.lastT = 0;

    var self = this;
    this.onResize = function () { self.sync(); };
    window.addEventListener('resize', this.onResize);
    this.sync();
  }

  Gallery.prototype.sync = function () {
    var want = window.innerWidth <= this.breakpoint;
    if (want && !this.built) this.build();
    else if (!want && this.built) this.teardown();
    if (this.built) this.measure();
  };

  Gallery.prototype.build = function () {
    var photos = this.media.querySelectorAll('.mb-photo');
    if (photos.length < 2) return;                   // nothing to scroll through

    /* Frame mode lives on the WRAPPER, which this script owns. Nothing is
       written onto a photo, so the desktop collage is still restored exactly
       when the rail comes down. */
    var mode = this.media.getAttribute('data-gal-frames');
    if (mode !== 'keep' && mode !== 'one') mode = 'plain';
    var cls = 'mb-gallery mb-gallery--' + mode;
    if (mode === 'one') {
      var pick = this.media.getAttribute('data-gal-frame') || 'pixel';
      if (!/^[a-z]+$/.test(pick)) pick = 'pixel';        // it is going into a class name
      if (pick !== 'none') cls += ' mb-gallery--frame-' + pick;
    }

    this.wrap = document.createElement('div');
    this.wrap.className = cls;
    this.track = document.createElement('div');
    this.track.className = 'mb-gallery__track';
    this.wrap.appendChild(this.track);

    /* The rail borrows the real photo nodes rather than rebuilding them, and
       every photo leaves a marker behind at its exact position in the column.
       Rotating back to desktop puts each node in front of its own marker, so
       the collage comes back byte-identical — same order, same frame classes,
       same inline position/tilt vars. The rail never writes to a photo; all of
       its styling hangs off `.mb-gallery__track >`, which stops matching the
       moment the node leaves the track. */
    this.originals = [];
    this.marks = [];
    for (var i = 0; i < photos.length; i++) {
      var mark = document.createComment('mb-gallery');
      photos[i].parentNode.insertBefore(mark, photos[i]);
      this.originals.push(photos[i]);
      this.marks.push(mark);
    }

    for (var k = 0; k < this.originals.length; k++) this.track.appendChild(this.originals[k]);

    // The second copy is what the loop wraps into.
    this.copy = [];
    for (var j = 0; j < this.originals.length; j++) {
      var c = this.originals[j].cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.classList.add('is-clone');
      this.copy.push(c);
      this.track.appendChild(c);
    }

    this.media.appendChild(this.wrap);
    this.media.classList.add('is-gallery');
    this.built = true;
    this.watchImages();
    this.bindDrag();
    this.run();
  };

  /* In the rail every photo is a fixed height and an AUTO width, so the width
     of one copy is not known until the images have their aspect ratios. The
     markup sets width/height attributes, so that is usually true immediately —
     but not for a lazy image that has not decoded yet, and a stale span shows
     up as a visible jump at the wrap. Re-measure as each one lands. */
  Gallery.prototype.watchImages = function () {
    var self = this;
    var imgs = this.track.querySelectorAll('img');
    var onLoad = function () { self.measure(); };
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].complete) continue;
      imgs[i].addEventListener('load', onLoad, { once: true });
      imgs[i].addEventListener('error', onLoad, { once: true });
    }
  };

  Gallery.prototype.teardown = function () {
    if (!this.built) return;
    cancelAnimationFrame(this.raf);
    for (var i = 0; i < this.originals.length; i++) {
      var mark = this.marks[i];
      if (mark && mark.parentNode) mark.parentNode.insertBefore(this.originals[i], mark);
    }
    for (var m = 0; m < this.marks.length; m++) {
      if (this.marks[m].parentNode) this.marks[m].parentNode.removeChild(this.marks[m]);
    }
    this.marks = [];
    if (this.wrap && this.wrap.parentNode) this.wrap.parentNode.removeChild(this.wrap);
    this.media.classList.remove('is-gallery');
    this.built = false;
    this.offset = 0;
    this.vel = 0;
  };

  /* Width of ONE copy of the run, including the gap that follows it. */
  Gallery.prototype.measure = function () {
    if (!this.built || !this.originals.length) return;
    var gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    var w = 0;
    for (var i = 0; i < this.originals.length; i++) {
      w += this.originals[i].getBoundingClientRect().width + gap;
    }
    this.span = Math.max(1, w);
  };

  Gallery.prototype.bindDrag = function () {
    var self = this;
    var startX = 0, startOffset = 0, lastX = 0, lastMove = 0;

    this.onDown = function (e) {
      self.dragging = true;
      self.vel = 0;
      startX = lastX = (e.touches ? e.touches[0].clientX : e.clientX);
      startOffset = self.offset;
      lastMove = performance.now();
      self.wrap.classList.add('is-grabbing');
    };
    this.onMove = function (e) {
      if (!self.dragging) return;
      var x = (e.touches ? e.touches[0].clientX : e.clientX);
      self.offset = startOffset + (x - startX);
      var now = performance.now();
      var dt = Math.max(1, now - lastMove);
      // px per second, so it hands over to the drift in the same units
      self.vel = (x - lastX) / dt * 1000;
      lastX = x; lastMove = now;
      self.awake = true;
    };
    this.onUp = function () {
      if (!self.dragging) return;
      self.dragging = false;
      self.wrap.classList.remove('is-grabbing');
    };

    this.wrap.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove, { passive: true });
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
    // Safari versions without pointer events on touch
    this.wrap.addEventListener('touchstart', this.onDown, { passive: true });
    window.addEventListener('touchmove', this.onMove, { passive: true });
    window.addEventListener('touchend', this.onUp);
  };

  Gallery.prototype.run = function () {
    var self = this;
    var tick = function (ts) {
      self.raf = requestAnimationFrame(tick);
      if (!self.built) return;
      var dt = self.lastT ? Math.min(64, ts - self.lastT) : 16;
      self.lastT = ts;

      if (!self.dragging) {
        if (Math.abs(self.vel) > 1) {
          // Throw decays back into the steady drift rather than stopping dead.
          self.offset += self.vel * (dt / 1000);
          self.vel *= Math.pow(0.0015, dt / 1000);
        } else if (!reduceMotion) {
          self.offset -= self.speed * (dt / 1000);
        }
      }

      /* Wrap modulo one copy. Because a second copy always sits to the right,
         the seam never becomes visible and nothing has to be re-appended. */
      if (self.span) {
        while (self.offset <= -self.span) self.offset += self.span;
        while (self.offset > 0) self.offset -= self.span;
      }
      self.track.style.transform = 'translate3d(' + self.offset.toFixed(2) + 'px,0,0)';
    };
    this.raf = requestAnimationFrame(tick);
  };

  Gallery.prototype.destroy = function () {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    window.removeEventListener('touchmove', this.onMove);
    window.removeEventListener('touchend', this.onUp);
    this.teardown();
  };

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-mb-gallery]').forEach(function (el) {
      if (el.__mbGallery) return;
      el.__mbGallery = new Gallery(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else { initAll(); }

  // Images settle after load; the span depends on their rendered width.
  window.addEventListener('load', function () {
    document.querySelectorAll('[data-mb-gallery]').forEach(function (el) {
      if (el.__mbGallery) el.__mbGallery.sync();
    });
  });

  document.addEventListener('shopify:section:load', function (e) {
    var old = e.target.querySelector('[data-mb-gallery]');
    if (old && old.__mbGallery) { old.__mbGallery.destroy(); old.__mbGallery = null; }
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-mb-gallery]');
    if (el && el.__mbGallery) { el.__mbGallery.destroy(); el.__mbGallery = null; }
  });

  window.MBGallery = Gallery;
})();
